import { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker } from '@tanstack/react-router';

interface UseUnsavedChangesGuardOptions {
  isDirty: boolean;
}

export function useUnsavedChangesGuard({ isDirty }: UseUnsavedChangesGuardOptions) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const bypassGuardRef = useRef(false);

  const blocker = useBlocker({
    shouldBlockFn: () => isDirty && !bypassGuardRef.current,
    withResolver: true,
  });

  useEffect(() => {
    if (blocker.status === 'blocked') {
      setIsConfirmOpen(true);
      pendingActionRef.current = () => blocker.proceed();
    }
  }, [blocker.status, blocker.proceed]);

  const allowNavigation = useCallback(() => {
    bypassGuardRef.current = true;
  }, []);

  const requestExit = useCallback(
    (action: () => void) => {
      if (!isDirty || bypassGuardRef.current) {
        action();
        return;
      }

      pendingActionRef.current = action;
      setIsConfirmOpen(true);
    },
    [isDirty],
  );

  const confirmExit = useCallback(() => {
    setIsConfirmOpen(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    bypassGuardRef.current = true;
    action?.();
  }, []);

  const cancelExit = useCallback(() => {
    pendingActionRef.current = null;
    setIsConfirmOpen(false);
    if (blocker.status === 'blocked') {
      blocker.reset();
    }
  }, [blocker.status, blocker.reset]);

  useEffect(() => {
    if (!isDirty || bypassGuardRef.current) return;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  return {
    isConfirmOpen,
    requestExit,
    confirmExit,
    cancelExit,
    allowNavigation,
  };
}
